import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalContent = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos información personal que nos proporcionas voluntariamente al realizar una compra, como tu nombre, dirección de correo electrónico y datos de pago. Esta información es necesaria para procesar tu pedido y entregarte el producto digital.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos tu información personal únicamente para procesar tu compra, enviarte el producto digital adquirido y comunicarnos contigo sobre tu pedido. No vendemos, alquilamos ni compartimos tu información personal con terceros para fines de marketing.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración. Todos los pagos se procesan a través de plataformas seguras con encriptación SSL.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio web puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales proporcionados en tu confirmación de compra.'
  }]
};
const termsContent = {
  title: 'Términos de Servicio',
  sections: [{
    heading: 'Aceptación de los Términos',
    text: 'Al acceder y utilizar este sitio web y realizar una compra, aceptas estar sujeto a estos términos de servicio. Si no estás de acuerdo con alguna parte de estos términos, te pedimos que no utilices nuestros servicios.'
  }, {
    heading: 'Producto Digital',
    text: 'El Método de la Pausa Consciente es un producto digital (ebook/guía) que se entrega electrónicamente. Una vez completada la compra, recibirás acceso inmediato al contenido. El producto está diseñado para uso personal y educativo.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Sin embargo, si experimentas problemas técnicos para acceder al contenido, nos comprometemos a resolverlos o proporcionar una solución alternativa.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido del producto, incluyendo textos, gráficos y metodologías, está protegido por derechos de autor. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'El contenido de este producto es de carácter informativo y educativo. Los resultados pueden variar según cada persona. No nos hacemos responsables por decisiones tomadas basadas en el contenido del producto.'
  }]
};
export function FooterSection() {
  const [modalContent, setModalContent] = useState<ModalContent>(null);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModalContent(null);
      }
    };
    if (modalContent) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [modalContent]);
  const content = modalContent === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-cream-secondary border-t border-gold/20" data-id="element-1">
        <div className="max-w-3xl mx-auto px-4 py-8" data-id="element-2">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-3">
            <p className="text-sm text-text-medium/60 text-center sm:text-left" data-id="element-4">
              © {new Date().getFullYear()} El Método de la Pausa Consciente.
              Todos los derechos reservados.
            </p>

            <div className="flex items-center gap-6" data-id="element-5">
              <button onClick={() => setModalContent('privacy')} className="text-sm text-text-medium underline underline-offset-2 hover:text-gold-dark transition-colors duration-200" data-id="element-6">
                Política de Privacidad
              </button>
              <button onClick={() => setModalContent('terms')} className="text-sm text-text-medium underline underline-offset-2 hover:text-gold-dark transition-colors duration-200" data-id="element-7">
                Términos de Servicio
              </button>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-8">
        {modalContent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalContent(null)} data-id="element-9">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2
        }} className="bg-cream w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-10">
              <div className="sticky top-0 bg-cream border-b border-gold/20 px-6 py-4 flex items-center justify-between" data-id="element-11">
                <h2 className="text-2xl font-heading text-text-dark" data-id="element-12">
                  {content.title}
                </h2>
                <button onClick={() => setModalContent(null)} className="p-2 rounded-full hover:bg-gold/10 transition-colors duration-200" aria-label="Cerrar" data-id="element-13">
                  <XIcon className="w-5 h-5 text-text-medium" data-id="element-14" />
                </button>
              </div>

              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-72px)]" data-id="element-15">
                <div className="space-y-6" data-id="element-16">
                  {content.sections.map((section, index) => <div key={index} data-id="element-17">
                      <h3 className="text-lg font-bold text-text-dark mb-2" data-id="element-18">
                        {section.heading}
                      </h3>
                      <p className="text-text-medium leading-relaxed" data-id="element-19">
                        {section.text}
                      </p>
                    </div>)}
                </div>

                <p className="mt-8 pt-6 border-t border-gold/20 text-sm text-text-medium/60" data-id="element-20">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                month: 'long',
                year: 'numeric'
              })}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}